/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useServiceContext } from '../../../../root'
import ModalConfirmation from '../../../../components/shared/ModalConfirmation'
import HtmlCell from '../../../../components/shared/HtmlCell'
import { toast } from '../../../../services/toast'
import { FolderType } from '../../../../services/api/api.foldertypes'

interface ModalRemoveProps {
  folderType: FolderType
  onClose: () => void
  onSuccess?: () => void
}

const ModalRemove: React.FC<ModalRemoveProps> = ({ folderType, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const [msg, setMsg] = useState<React.ReactNode>('')
  const [loading, setLoading] = useState<Promise<any> | null>(null)

  useEffect(() => {
    selectMessage()
  }, [])

  const selectMessage = async () => {
    try {
      const promise = context.api.folderTypes.folderUsage(folderType.id)
      setLoading(promise)

      const data = (await promise) as { value?: string[] }
      const usedIn = data.value || []

      const textMsg = (
        <div>
          <HtmlCell>
            {usedIn && usedIn.length > 0
              ? translate('FOLDER_TYPES_DELETE_MSG_CANNOT_BE_DELETED', { name: folderType.name }) +
                ' ' +
                usedIn.join('<br/> ')
              : translate('SETTINGS_FOLDER_REMOVE') + ' <b>' + folderType.name + '</b>'}
          </HtmlCell>
        </div>
      )

      setMsg(textMsg)
      setLoading(null)
    } catch (error) {
      setLoading(null)
    }
  }

  const doSave = async () => {
    try {
      await context.api.folderTypes.remove(folderType.id)
      toast.success(translate('FOLDER_TYPES_DELETE_SUCCESS_MSG'))

      if (onSuccess) {
        onSuccess()
      }
      onClose()
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.message || 'Unknown error'
      toast.error(translate('FOLDER_TYPES_DELETE_FAIL_MSG') + ': ' + errorMsg)
    }
  }

  return (
    <ModalConfirmation
      titleMsg={translate('REMOVE')}
      loading={loading}
      msg={msg}
      cancelTxt={translate('CANCEL')}
      okTxt={translate('OK')}
      onSubmit={doSave}
      onCancel={onClose}
    />
  )
}

export default ModalRemove
